import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { client, useAuth, useI18n } from 'iomart-hooks';
import Iconify from 'src/components/iconify/Iconify';

export const WB_CATALOG_RELOAD_EVENT = 'iomart:wb-catalog-products-reload';
type AccountOption = { id: number; name: string; hasApiKey: boolean };
const formatDateTime = (value?: string | null) => (!value ? '—' : dayjs(value).format('DD.MM.YYYY HH:mm:ss'));

export function WbCatalogProductsSyncToolbar() {
  const { role, userInfo } = useAuth(false) as any;
  const { t } = useI18n(false);
  const { enqueueSnackbar } = useSnackbar();
  const enqueueSnackbarRef = useRef(enqueueSnackbar);
  enqueueSnackbarRef.current = enqueueSnackbar;
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [account, setAccount] = useState<AccountOption | null>(null);
  const [historyAccount, setHistoryAccount] = useState<AccountOption | null>(null);
  const [pageLimit, setPageLimit] = useState<number>(20);
  const [syncing, setSyncing] = useState(false);
  const [historyRuns, setHistoryRuns] = useState<any[]>([]);
  const [autoTasks, setAutoTasks] = useState<any[]>([]);
  const [taskActionLoadingId, setTaskActionLoadingId] = useState<number | null>(null);
  const [creatingAutoTask, setCreatingAutoTask] = useState(false);
  const [intervalMinutes, setIntervalMinutes] = useState<number>(60);
  const isAdmin = String(role ?? userInfo?.role ?? '').toUpperCase() === 'ADMIN';

  const loadAccounts = useCallback(async () => {
    const res = await (client as any).query({
      listWbSellerAccounts: {
        items: { id: true, name: true, hasApiKey: true },
        __args: { category: 'all', paging: { page: 0, size: 500 }, sorting: [{ by: 'asc', order: 'name' }], filters: {} },
      },
    });
    setAccounts((res?.listWbSellerAccounts?.items ?? []) as AccountOption[]);
  }, []);

  const loadHistoryRuns = useCallback(async (accountId: number) => {
    const res = await (client as any).query({
      wbCatalogSyncRuns: {
        __args: { data: { accountId, page: 0, size: 100 } },
        items: { id: true, status: true, totalFetched: true, totalUpserted: true, errorSummary: true, startedAt: true, finishedAt: true, createdAt: true },
      },
    });
    setHistoryRuns(res?.wbCatalogSyncRuns?.items ?? []);
  }, []);

  const loadAutoTasks = useCallback(async (accountId?: number) => {
    const res = await (client as any).query({
      wbCatalogAutoSyncTasks: {
        __args: { data: { accountId } },
        id: true,
        status: true,
        intervalMinutes: true,
        pageLimit: true,
        lastRunAt: true,
        nextRunAt: true,
      },
    });
    setAutoTasks(res?.wbCatalogAutoSyncTasks ?? []);
  }, []);

  useEffect(() => {
    if (!open && !historyOpen) return;
    loadAccounts();
  }, [open, historyOpen, loadAccounts]);

  useEffect(() => {
    if (open && !account && accounts.length > 0) setAccount(accounts[0]);
    if (historyOpen && !historyAccount && accounts.length > 0) {
      setHistoryAccount(accounts[0]);
      loadHistoryRuns(accounts[0].id);
      loadAutoTasks(accounts[0].id);
    }
  }, [open, historyOpen, account, historyAccount, accounts, loadHistoryRuns, loadAutoTasks]);

  const handleSync = async () => {
    if (!account?.id) return;
    setSyncing(true);
    try {
      const res = await (client as any).mutation({
        syncWbCatalogProducts: {
          count: true,
          __args: { data: { accountId: account.id, pageLimit: Math.max(1, Number(pageLimit || 1)) } },
        },
      });
      enqueueSnackbarRef.current(t('Synced {{count}} WB catalog cards', { count: res?.syncWbCatalogProducts?.count ?? 0 }), { variant: 'success' });
      window.dispatchEvent(new Event(WB_CATALOG_RELOAD_EVENT));
      setOpen(false);
    } catch (e: any) {
      enqueueSnackbarRef.current(e?.message ?? t('Catalog sync failed'), { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  return !isAdmin ? null : (
    <>
      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="contained" onClick={() => setOpen(true)}>{t('Sync catalog')}</Button>
        <IconButton size="small" onClick={() => setHistoryOpen(true)}>
          <Iconify icon="mdi:history" width={18} />
        </IconButton>
      </Stack>
      <Drawer anchor="right" open={open} onClose={() => !syncing && setOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 520 }, p: 2 }}>
          <Stack spacing={2.5}>
            <Typography variant="h6">{t('Sync WB catalog')}</Typography>
            <Autocomplete
              options={accounts}
              value={account}
              onChange={(_, v) => setAccount(v)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label={t('WB account')} size="small" required />}
            />
            <TextField
              size="small"
              type="number"
              label={t('Pages limit')}
              value={pageLimit}
              onChange={(e) => setPageLimit(Math.max(1, Number(e.target.value || 1)))}
            />
            <LoadingButton loading={syncing} variant="contained" onClick={handleSync}>{t('Run sync')}</LoadingButton>
          </Stack>
        </Box>
      </Drawer>
      <Drawer anchor="right" open={historyOpen} onClose={() => setHistoryOpen(false)}>
        <Box sx={{ width: { xs: '100vw', sm: 900 }, p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="h6">{t('Catalog sync history')}</Typography>
            <Autocomplete
              options={accounts}
              value={historyAccount}
              onChange={(_, v) => {
                setHistoryAccount(v);
                if (v?.id) {
                  loadHistoryRuns(v.id);
                  loadAutoTasks(v.id);
                }
              }}
              getOptionLabel={(o) => o.name}
              renderInput={(params) => <TextField {...params} label={t('WB account')} size="small" />}
            />
            <Paper variant="outlined" sx={{ p: 1.5 }}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">{t('Auto sync')}</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField size="small" type="number" label={t('Periodicity (minutes)')} value={intervalMinutes} onChange={(e) => setIntervalMinutes(Math.max(1, Number(e.target.value || 1)))} />
                  <TextField size="small" type="number" label={t('Pages limit')} value={pageLimit} onChange={(e) => setPageLimit(Math.max(1, Number(e.target.value || 1)))} />
                  <LoadingButton
                    size="small"
                    loading={creatingAutoTask}
                    disabled={!historyAccount?.id}
                    onClick={async () => {
                      if (!historyAccount?.id) return;
                      setCreatingAutoTask(true);
                      try {
                        await (client as any).mutation({
                          createWbCatalogAutoSyncTask: {
                            id: true,
                            __args: { data: { accountId: historyAccount.id, intervalMinutes, pageLimit } },
                          },
                        });
                        enqueueSnackbarRef.current(t('Auto task created'), { variant: 'success' });
                        loadAutoTasks(historyAccount.id);
                      } catch (e: any) {
                        enqueueSnackbarRef.current(e?.message ?? t('Failed to create auto task'), { variant: 'error' });
                      } finally {
                        setCreatingAutoTask(false);
                      }
                    }}
                  >
                    {t('Create')}
                  </LoadingButton>
                </Stack>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Status</TableCell><TableCell align="right">Interval</TableCell><TableCell align="right">Pages</TableCell><TableCell>Next run</TableCell><TableCell align="right">Actions</TableCell></TableRow></TableHead>
                    <TableBody>
                      {autoTasks.map((task) => (
                        <TableRow key={task.id}>
                          <TableCell>{task.id}</TableCell>
                          <TableCell>{task.status}</TableCell>
                          <TableCell align="right">{task.intervalMinutes}m</TableCell>
                          <TableCell align="right">{task.pageLimit}</TableCell>
                          <TableCell>{formatDateTime(task.nextRunAt)}</TableCell>
                          <TableCell align="right">
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <LoadingButton size="small" color="warning" loading={taskActionLoadingId === task.id} disabled={task.status !== 'active'} onClick={async () => {
                                setTaskActionLoadingId(task.id);
                                try {
                                  await (client as any).mutation({ stopWbCatalogAutoSyncTask: { __args: { id: task.id } } });
                                  if (historyAccount?.id) loadAutoTasks(historyAccount.id);
                                } finally {
                                  setTaskActionLoadingId(null);
                                }
                              }}>{t('Stop')}</LoadingButton>
                              <LoadingButton size="small" color="error" loading={taskActionLoadingId === task.id} onClick={async () => {
                                setTaskActionLoadingId(task.id);
                                try {
                                  await (client as any).mutation({ deleteWbCatalogAutoSyncTask: { __args: { id: task.id } } });
                                  if (historyAccount?.id) loadAutoTasks(historyAccount.id);
                                } finally {
                                  setTaskActionLoadingId(null);
                                }
                              }}>{t('Delete')}</LoadingButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Paper>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead><TableRow><TableCell>ID</TableCell><TableCell>Status</TableCell><TableCell align="right">Fetched</TableCell><TableCell align="right">Upserted</TableCell><TableCell>Started</TableCell><TableCell>Finished</TableCell><TableCell>Error</TableCell></TableRow></TableHead>
                <TableBody>
                  {historyRuns.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell align="right">{r.totalFetched}</TableCell>
                      <TableCell align="right">{r.totalUpserted}</TableCell>
                      <TableCell>{formatDateTime(r.startedAt)}</TableCell>
                      <TableCell>{formatDateTime(r.finishedAt)}</TableCell>
                      <TableCell>{r.errorSummary ?? ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </Box>
      </Drawer>
    </>
  );
}

